import React, { useEffect } from 'react'
import { Grid, GridItem } from '@deal/components'
import { useFullBrowseNavHierarchyQuery } from '../../../FullBrowseNavHierarchy.generated'
import { useExperiment } from '@deal/experiment-js'
import { useNavigationContext } from '../../../NavigationContext'
import { useDepartmentContext } from '#src/context/department'
import constructFlatDepartments from '../../../helpers/constructFlatDepartments'
import DepartmentsColumn from './DepartmentsColumn'
import SubSectionsColumn from './SubSectionsColumn'
import TopLists from '../../../TopLists'
import styles from './styles.css'

interface BrowseMenuProps {
  isVisible: boolean
}

const BrowseMenu: React.FC<BrowseMenuProps> = ({ isVisible }) => {
  const navContext = useNavigationContext()
  const departmentContext = useDepartmentContext()

  const { data } = useFullBrowseNavHierarchyQuery()

  // Don't track experiment viewed event unless is visible
  const { result: navBrowseTopListResult } = useExperiment({
    experiment: 'nav-browse-top-list',
    defaultTreatment: 'off',
    options: {
      skip: !isVisible
    }
  })

  /**
   * When we expand the browse menu we want the currently selected department to be the
   * one we're in. In the event it's not a true department (winter-sports), then select
   * the first navigation item available. Not totally ideal, but sufficies until we have
   * some kind persistant store of where the user is within a department.
   */
  useEffect(() => {
    const currentDeptHierarchy = data?.departmentsForNavigation.find(department => {
      return department.slug === departmentContext.department?.slug
    })

    if (!currentDeptHierarchy) {
      return
    }

    if (currentDeptHierarchy.navigations.length > 1) {
      navContext?.onSetActiveDepartmentSlug(currentDeptHierarchy.navigations[0].category?.slug)
    } else {
      navContext?.onSetActiveDepartmentSlug(currentDeptHierarchy.slug)
    }
  }, [isVisible])

  if (!data) {
    return null
  }

  const flattenedDepartments = constructFlatDepartments(data.departmentsForNavigation)

  return (
    <div className={styles.menu} data-testid="desktop-browse-menu">
      <Grid wrapperClassName={styles.wrapper}>
        {/* Extra left padding for desktop */}
        <GridItem col={2} mdCol={1} />
        <GridItem col={2} mdCol={2} className={styles.column}>
          <DepartmentsColumn flatDepartments={flattenedDepartments} />
        </GridItem>

        <GridItem
          col={2}
          mdCol={navBrowseTopListResult === 'v1' ? 5 : 9}
          // If we are in v1 and showing top list module, we want sub sections to take of 50% width into 2 columns
          className={styles.subSectionWrapper}
          data-testid="desktop-categories-menu"
        >
          {flattenedDepartments.map((flatDepartment, departmentIndex) => {
            // The active department is stored in context. If no context is available, we select the
            //   first department by default.
            let isActiveDepartmentFromContext =
              navContext?.activeDepartmentSlug === flatDepartment.referenceSlug
            let isActiveDepartmentByDefault =
              !navContext?.activeDepartmentSlug && departmentIndex === 0

            return (
              <SubSectionsColumn
                key={`${flatDepartment.id}-${departmentIndex}`}
                topLevelItems={flatDepartment.topLevelItems}
                isTwoColumn={navBrowseTopListResult === 'v1'}
                isVisible={isActiveDepartmentFromContext || isActiveDepartmentByDefault}
              />
            )
          })}
        </GridItem>

        {navBrowseTopListResult === 'v1' && (
          <GridItem col={2} mdCol={4}>
            <TopLists className={styles.topList} isVisible={isVisible} />
          </GridItem>
        )}
      </Grid>
    </div>
  )
}

export default BrowseMenu
